import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { addDays, format, isSameDay, startOfDay, subDays } from 'date-fns';
import { Link } from 'react-router-dom';
import { GET_PROPERTY_RESERVATIONS, GET_USERS, GET_WORK_ORDER_FEED } from '../graphql/queries';
import { useAuth } from '../contexts/AuthContext';
import WorkOrderAreaFilter from '../components/work-orders/WorkOrderAreaFilter';
import WorkOrderRoleFilter from '../components/work-orders/WorkOrderRoleFilter';
import WorkOrderListItem from '../components/work-orders/WorkOrderListItem';
import WorkOrderDetailSlidePanel from '../components/work-orders/WorkOrderDetailSlidePanel';
import PropertyManagementFilter from '../components/properties/PropertyManagementFilter';
import { parseManagementFilter } from '../utils/propertyManagers';
import { sortWorkOrdersByPriority } from '../utils/work-order-occupancy';
import { UserRole } from '../utils/enums';

const ITEMS_PER_PAGE = 50;

const AREA_LABELS = {
  KIAWAH: 'Kiawah',
  ISLE_OF_PALMS: 'Isle of Palms',
  ISLE_OF_PALMS_WILD_DUNES: 'Isle of Palms - Wild Dunes',
  SEABROOK_ISLAND: 'Seabrook Island',
};

const DEPARTMENT_FILTERS = [
  {
    label: 'Property Management',
    roles: [UserRole.PROPERTY_MANAGER, UserRole.MAINTENANCE],
  },
  {
    label: 'Housekeeping',
    roles: [UserRole.HOUSEKEEPER_MANAGER],
  },
];

// Roles that should default the department (role) filter to their own department.
// Admins / call-center users see all departments by default.
const DEFAULT_ROLE_FILTER_BY_USER_ROLE = {
  [UserRole.HOUSEKEEPER_MANAGER]: [UserRole.HOUSEKEEPER_MANAGER],
  [UserRole.PROPERTY_MANAGER]: [UserRole.PROPERTY_MANAGER, UserRole.MAINTENANCE],
  [UserRole.MAINTENANCE]: [UserRole.PROPERTY_MANAGER, UserRole.MAINTENANCE],
};

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateKey = (dateKey) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return startOfDay(new Date(year, month - 1, day));
};

const getStatusColor = (status) => {
  switch (status) {
    case 'OPEN':
      return 'bg-yellow-100 text-yellow-800';
    case 'IN_PROGRESS':
      return 'bg-blue-100 text-blue-800';
    case 'COMPLETED':
      return 'bg-green-100 text-green-800';
    case 'CANCELED':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const FilterToggle = ({ checked, onChange, label, count, accent = 'blue' }) => {
  const activeClasses = accent === 'red'
    ? 'peer-checked:bg-red-500 peer-focus:ring-red-100'
    : 'peer-checked:bg-blue-600 peer-focus:ring-blue-100';

  const countClasses = checked
    ? (accent === 'red' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800')
    : 'bg-gray-100 text-gray-700';

  return (
    <label className="flex min-h-12 cursor-pointer items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 hover:bg-gray-50">
      <span className="flex min-w-0 items-center gap-3">
        <span className="relative inline-flex shrink-0 items-center">
          <input
            type="checkbox"
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
            className="sr-only peer"
          />
          <span className={`h-6 w-11 rounded-full bg-gray-300 transition-colors peer-focus:outline-none peer-focus:ring-4 ${activeClasses}`}></span>
          <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full border border-gray-300 bg-white shadow transition-transform peer-checked:translate-x-5"></span>
        </span>
        <span className="min-w-0 text-sm font-medium text-gray-800">{label}</span>
      </span>
      {count !== undefined && (
        <span className={`inline-flex min-w-7 shrink-0 items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold ${countClasses}`}>
          {count}
        </span>
      )}
    </label>
  );
};

const FeedCountChip = ({ label, value, tone }) => {
  const toneClasses = {
    past: 'border-amber-200 bg-amber-100 text-amber-900',
    new: 'border-emerald-200 bg-emerald-100 text-emerald-900',
    due: 'border-indigo-200 bg-indigo-100 text-indigo-900',
  };

  return (
    <div className={`inline-flex min-w-0 items-center gap-2 rounded-full border px-3 py-1.5 ${toneClasses[tone]}`}>
      <span className="text-base font-semibold leading-none">{value}</span>
      <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
    </div>
  );
};

const ActiveFilterSummary = ({ filters, onClearAll }) => {
  if (filters.length === 0) return null;

  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/80 px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-sm font-semibold text-gray-700">Active filters:</span>
        {filters.map((filter) => (
          <button
            key={filter.key}
            type="button"
            onClick={filter.onRemove}
            className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-blue-200 bg-white px-3 py-1 text-sm font-medium text-blue-900 shadow-sm hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <span>{filter.label}</span>
            <svg className="h-3.5 w-3.5 text-blue-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            <span className="sr-only">Remove {filter.label} filter</span>
          </button>
        ))}
        <button
          type="button"
          onClick={onClearAll}
          className="ml-auto inline-flex min-h-8 items-center rounded-md border border-blue-200 bg-white px-3 py-1 text-sm font-medium text-blue-700 shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Clear all
        </button>
      </div>
    </div>
  );
};

export default function WorkOrders() {
  const { user: currentUser } = useAuth();
  const todayKey = useMemo(() => formatDateKey(new Date()), []);
  const canViewAllWorkOrders = currentUser?.role === UserRole.CALL_CENTER;

  const [selectedDateKey, setSelectedDateKey] = useState(todayKey);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [hasUserInteractedWithAreas, setHasUserInteractedWithAreas] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [hasUserInteractedWithRoles, setHasUserInteractedWithRoles] = useState(false);
  const [selectedPropertyManagerId, setSelectedPropertyManagerId] = useState('');
  const [includeOtherTeamMembers, setIncludeOtherTeamMembers] = useState(false);
  const [feedbackOnly, setFeedbackOnly] = useState(false);
  const [showAllFuture, setShowAllFuture] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isMobileActionsOpen, setIsMobileActionsOpen] = useState(false);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(null);
  const [isSlidePanelOpen, setIsSlidePanelOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [accumulatedWorkOrders, setAccumulatedWorkOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [reservationsByProperty, setReservationsByProperty] = useState({});

  const mobileActionsRef = useRef(null);
  const [fetchPropertyReservations] = useLazyQuery(GET_PROPERTY_RESERVATIONS, { fetchPolicy: 'network-only' });
  const { data: usersData } = useQuery(GET_USERS, {
    fetchPolicy: 'cache-first',
  });

  const propertyManagers = useMemo(() => {
    return (usersData?.users || [])
      .filter(user =>
        user.role === UserRole.PROPERTY_MANAGER &&
        user.isActive !== false
      )
      .sort((a, b) => {
        const nameA = a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email;
        const nameB = b.name || `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.email;
        return nameA.localeCompare(nameB);
      });
  }, [usersData?.users]);

  useEffect(() => {
    if (hasUserInteractedWithAreas) return;

    const userAreas = Array.isArray(currentUser?.areas) ? currentUser.areas : [];
    if (userAreas.length === 0) return;

    setSelectedAreas((previousAreas) => {
      const isSameSelection =
        previousAreas.length === userAreas.length &&
        previousAreas.every((area) => userAreas.includes(area));

      return isSameSelection ? previousAreas : userAreas;
    });
  }, [currentUser?.areas, hasUserInteractedWithAreas]);

  useEffect(() => {
    if (hasUserInteractedWithRoles) return;
    const defaultRoles = DEFAULT_ROLE_FILTER_BY_USER_ROLE[currentUser?.role];
    if (!defaultRoles || defaultRoles.length === 0) return;

    setSelectedRoles((previousRoles) => {
      if (previousRoles.length > 0) return previousRoles;
      return defaultRoles;
    });
  }, [currentUser?.role, hasUserInteractedWithRoles]);

  useEffect(() => {
    if (!isMobileActionsOpen) return;

    const handleInteraction = (event) => {
      if (
        mobileActionsRef.current &&
        !mobileActionsRef.current.contains(event.target)
      ) {
        setIsMobileActionsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMobileActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMobileActionsOpen]);

  useEffect(() => {
    if (localSearch === searchTerm) return;

    const timer = setTimeout(() => {
      setSearchTerm(localSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearch, searchTerm]);

  const commonFeedInput = useMemo(() => ({
    date: showAllFuture ? todayKey : selectedDateKey,
    showAllFuture,
    search: searchTerm || undefined,
    ...parseManagementFilter(selectedPropertyManagerId),
    includeOtherTeamMembers: includeOtherTeamMembers || undefined,
    areas: selectedAreas.length > 0 ? selectedAreas : undefined,
    assignedRoles: selectedRoles.length > 0 ? selectedRoles : undefined,
    limit: ITEMS_PER_PAGE,
  }), [
    includeOtherTeamMembers,
    searchTerm,
    selectedAreas,
    selectedDateKey,
    selectedPropertyManagerId,
    selectedRoles,
    showAllFuture,
    todayKey,
  ]);

  const baseFeedInput = useMemo(() => ({
    ...commonFeedInput,
    feedbackMode: feedbackOnly ? 'ONLY' : 'HIDE',
  }), [commonFeedInput, feedbackOnly]);

  const feedbackCountInput = useMemo(() => ({
    ...commonFeedInput,
    feedbackMode: 'ONLY',
    limit: 1,
  }), [commonFeedInput]);

  const feedKey = useMemo(() => JSON.stringify(baseFeedInput), [baseFeedInput]);

  const {
    loading,
    error,
    data,
    fetchMore,
    refetch,
  } = useQuery(GET_WORK_ORDER_FEED, {
    variables: {
      input: {
        ...baseFeedInput,
        page: 1,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
    onCompleted: (result) => {
      setAccumulatedWorkOrders(result?.workOrderFeed?.items || []);
      setPage(1);
    },
  });

  const { data: feedbackCountData } = useQuery(GET_WORK_ORDER_FEED, {
    variables: {
      input: {
        ...feedbackCountInput,
        page: 1,
      },
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setAccumulatedWorkOrders([]);
    setPage(1);
    setIsLoadingMore(false);
  }, [feedKey]);

  useEffect(() => {
    const propertyIds = [
      ...new Set(
        accumulatedWorkOrders
          .map((workOrder) => workOrder.property?.id)
          .filter(Boolean)
      ),
    ];
    const idsToFetch = propertyIds.filter((id) => !reservationsByProperty[id]);
    if (idsToFetch.length === 0) return;

    const fetchAll = async () => {
      const results = await Promise.allSettled(
        idsToFetch.map((id) => fetchPropertyReservations({ variables: { propertyId: id } }))
      );
      setReservationsByProperty((prev) => {
        const next = { ...prev };
        results.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value?.data?.propertyReservations) {
            next[idsToFetch[index]] = result.value.data.propertyReservations;
          }
        });
        return next;
      });
    };

    fetchAll();
  }, [accumulatedWorkOrders, fetchPropertyReservations, reservationsByProperty]);

  const total = data?.workOrderFeed?.total || 0;
  const pastCount = data?.workOrderFeed?.pastCount || 0;
  const newCount = data?.workOrderFeed?.newCount || 0;
  const dueTodayCount = data?.workOrderFeed?.dueTodayCount || 0;
  const feedbackCount = feedbackCountData?.workOrderFeed?.total || 0;
  const hasMore = accumulatedWorkOrders.length < total;
  const selectedDate = useMemo(() => parseDateKey(selectedDateKey), [selectedDateKey]);
  const isSelectedToday = isSameDay(selectedDate, parseDateKey(todayKey));

  const displayedWorkOrders = useMemo(() => {
    return sortWorkOrdersByPriority(accumulatedWorkOrders, reservationsByProperty, currentUser?.id);
  }, [accumulatedWorkOrders, currentUser?.id, reservationsByProperty]);

  const handleViewWorkOrder = useCallback((workOrderId) => {
    setSelectedWorkOrderId(workOrderId);
    setIsSlidePanelOpen(true);
  }, []);

  const handleCloseSlidePanel = useCallback(() => {
    setIsSlidePanelOpen(false);
    setSelectedWorkOrderId(null);
  }, []);

  const handleWorkOrderDataChange = useCallback(() => {
    refetch();
  }, [refetch]);

  const handlePreviousDate = () => {
    if (isSelectedToday || showAllFuture) return;
    const previous = subDays(selectedDate, 1);
    const previousKey = formatDateKey(previous);
    setSelectedDateKey(previousKey < todayKey ? todayKey : previousKey);
  };

  const handleNextDate = () => {
    if (showAllFuture) return;
    setSelectedDateKey(formatDateKey(addDays(selectedDate, 1)));
  };

  const handleGoToToday = () => {
    setShowAllFuture(false);
    setSelectedDateKey(todayKey);
  };

  const handleDatePickerChange = (event) => {
    const nextDateKey = event.target.value;
    if (!nextDateKey) return;

    setShowAllFuture(false);
    setSelectedDateKey(nextDateKey < todayKey ? todayKey : nextDateKey);
  };

  const handleAreaFilterChange = (nextAreas) => {
    setHasUserInteractedWithAreas(true);
    setSelectedAreas(nextAreas);
  };

  const handleRoleFilterChange = (nextRoles) => {
    setHasUserInteractedWithRoles(true);
    setSelectedRoles(nextRoles);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setLocalSearch('');
    setHasUserInteractedWithAreas(true);
    setSelectedAreas([]);
    setSelectedRoles([]);
    setHasUserInteractedWithRoles(true);
    setSelectedPropertyManagerId('');
    setIncludeOtherTeamMembers(false);
    setFeedbackOnly(false);
    setShowAllFuture(false);
  };

  const handleClearSearch = () => {
    setLocalSearch('');
    setSearchTerm('');
  };

  const activeFilters = [];
  const trimmedSearch = searchTerm.trim();

  if (trimmedSearch) {
    activeFilters.push({
      key: 'search',
      label: `Search: ${trimmedSearch}`,
      onRemove: handleClearSearch,
    });
  }

  selectedAreas.forEach((area) => {
    activeFilters.push({
      key: `area-${area}`,
      label: `Area: ${AREA_LABELS[area] || area.replace(/_/g, ' ')}`,
      onRemove: () => handleAreaFilterChange(selectedAreas.filter((selectedArea) => selectedArea !== area)),
    });
  });

  const selectedDepartments = DEPARTMENT_FILTERS.filter((department) =>
    department.roles.some((role) => selectedRoles.includes(role))
  );

  selectedDepartments.forEach((department) => {
    activeFilters.push({
      key: `department-${department.label}`,
      label: `Department: ${department.label}`,
      onRemove: () => {
        const rolesToRemove = new Set(department.roles);
        handleRoleFilterChange(selectedRoles.filter((role) => !rolesToRemove.has(role)));
      },
    });
  });

  if (selectedPropertyManagerId) {
    const selectedFilter = parseManagementFilter(selectedPropertyManagerId);
    const selectedManager = propertyManagers.find((manager) => String(manager.id) === String(selectedFilter.propertyManagerId));
    const managerName =
      selectedManager?.name ||
      [selectedManager?.firstName, selectedManager?.lastName].filter(Boolean).join(' ') ||
      selectedManager?.email ||
      (selectedFilter.propertyManagementTeamId ? 'Selected team' : 'Selected property manager');

    activeFilters.push({
      key: 'property-manager',
      label: `Property Manager: ${managerName}`,
      onRemove: () => setSelectedPropertyManagerId(''),
    });
  }

  if (includeOtherTeamMembers) {
    activeFilters.push({
      key: 'include-other-team-members',
      label: 'Display All Assigned',
      onRemove: () => setIncludeOtherTeamMembers(false),
    });
  }

  if (showAllFuture) {
    activeFilters.push({
      key: 'show-all-future',
      label: 'Show all future work orders',
      onRemove: () => setShowAllFuture(false),
    });
  }

  if (feedbackOnly) {
    activeFilters.push({
      key: 'feedback-only',
      label: 'Feedback Work Orders',
      onRemove: () => setFeedbackOnly(false),
    });
  }

  const activeFilterCount = activeFilters.length;

  const loadMore = async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const nextPage = page + 1;
      const result = await fetchMore({
        variables: {
          input: {
            ...baseFeedInput,
            page: nextPage,
          },
        },
      });
      const newItems = result?.data?.workOrderFeed?.items || [];
      if (newItems.length > 0) {
        setAccumulatedWorkOrders((prev) => {
          const seen = new Set(prev.map((workOrder) => workOrder.id));
          const deduped = newItems.filter((workOrder) => !seen.has(workOrder.id));
          return [...prev, ...deduped];
        });
        setPage(nextPage);
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  const renderFeedSummary = () => {
    const feedLabel = feedbackOnly ? 'feedback work order' : 'work order';

    if (showAllFuture) {
      return (
        <span>
          <span className="font-semibold text-gray-900">{total}</span> future {feedLabel}{total === 1 ? '' : 's'}
        </span>
      );
    }

    return (
      <span>
        <span className="font-semibold text-gray-900">{total}</span> {feedLabel}{total === 1 ? '' : 's'} for{' '}
        <span className="font-medium text-gray-900">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div className="flex items-center justify-between sm:justify-start">
          <h1 className="text-2xl font-semibold text-white lg:text-gray-900">Work Orders</h1>

          <div className="relative sm:hidden" ref={mobileActionsRef}>
            <button
              type="button"
              onClick={() => setIsMobileActionsOpen((open) => !open)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/20 bg-white/10 text-white shadow-sm hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
              aria-label="Open work order actions"
              aria-haspopup="menu"
              aria-expanded={isMobileActionsOpen}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {isMobileActionsOpen && (
              <div
                className="absolute right-0 z-30 mt-2 w-56 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
                role="menu"
                aria-label="Work order actions"
              >
                {canViewAllWorkOrders && (
                  <Link
                    to="/dashboard/work-orders-all-status"
                    className="flex items-center px-4 py-3 text-sm font-medium text-blue-700 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                    role="menuitem"
                    onClick={() => setIsMobileActionsOpen(false)}
                  >
                    <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    All Work Orders
                  </Link>
                )}
                <Link
                  to="/dashboard/past-work-orders"
                  className="flex items-center px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                  role="menuitem"
                  onClick={() => setIsMobileActionsOpen(false)}
                >
                  <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Past Orders
                </Link>
                <Link
                  to="/dashboard/work-orders/new"
                  className="flex items-center px-4 py-3 text-sm font-medium text-blue-700 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                  role="menuitem"
                  onClick={() => setIsMobileActionsOpen(false)}
                >
                  <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  New Order
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="hidden sm:flex sm:flex-row sm:space-x-2">
          {canViewAllWorkOrders && (
            <Link
              to="/dashboard/work-orders-all-status"
              className="inline-flex items-center px-3 py-1.5 text-sm border border-blue-200 text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              All Work Orders
            </Link>
          )}
          <Link
            to="/dashboard/past-work-orders"
            className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Past Orders
          </Link>
          <Link
            to="/dashboard/work-orders/new"
            className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            New Order
          </Link>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg border border-gray-100 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {showAllFuture ? 'Future work orders' : 'Scheduled feed'}
            </p>
            <h2 className="mt-1 text-xl font-semibold text-gray-900">
              {showAllFuture ? 'All Future Work Orders' : format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </h2>
            {showAllFuture ? (
              <p className="mt-1 text-sm text-gray-500">{renderFeedSummary()}</p>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2">
                <FeedCountChip label="Past" value={pastCount} tone="past" />
                <FeedCountChip label="New" value={newCount} tone="new" />
                <FeedCountChip
                  label={isSelectedToday ? 'Due Date Set Today' : 'Due Date Set This Day'}
                  value={dueTodayCount}
                  tone="due"
                />
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-start gap-2 sm:justify-end">
            {!showAllFuture && (
              <label className="relative inline-flex w-full items-center sm:w-auto">
                <span className="sr-only">Jump to date</span>
                <svg className="pointer-events-none absolute left-3 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <input
                  type="date"
                  min={todayKey}
                  value={selectedDateKey}
                  onChange={handleDatePickerChange}
                  className="h-10 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto"
                  aria-label="Jump to scheduled work order date"
                />
              </label>
            )}
            {!showAllFuture && !isSelectedToday && (
              <button
                type="button"
                onClick={handleGoToToday}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Today
              </button>
            )}
            <button
              type="button"
              onClick={handlePreviousDate}
              disabled={isSelectedToday || showAllFuture}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Previous day"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={handleNextDate}
              disabled={showAllFuture}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Next day"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-visible rounded-lg border border-gray-100 bg-white shadow">
        <div className={`flex items-center gap-3 px-4 py-3 transition-colors duration-150 ${isFiltersOpen ? 'border-b border-gray-200' : ''}`}>
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-4"
            onClick={() => setIsFiltersOpen((open) => !open)}
            aria-expanded={isFiltersOpen}
            aria-controls="work-orders-feed-filters"
          >
            <div className="flex items-center">
              <h3 className="text-lg font-medium text-gray-900">Filters</h3>
              {activeFilterCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {activeFilterCount} active
                </span>
              )}
            </div>
            <svg
              className={`h-5 w-5 shrink-0 text-gray-400 transform transition-transform duration-150 ${isFiltersOpen ? 'rotate-180' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="inline-flex shrink-0 items-center rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              Clear All
            </button>
          )}
        </div>

        {isFiltersOpen && (
          <div id="work-orders-feed-filters" className="p-4 space-y-4">
            <div>
              <label htmlFor="work-orders-search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                  </svg>
                </div>
                <input
                  id="work-orders-search"
                  type="text"
                  value={localSearch}
                  onChange={(event) => setLocalSearch(event.target.value)}
                  placeholder="Search by property or description..."
                  aria-label="Search work orders by property or description"
                  className="w-full pl-9 pr-9 py-2 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                />
                {localSearch && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
              <div>
                <WorkOrderAreaFilter
                  selectedAreas={selectedAreas}
                  onChange={handleAreaFilterChange}
                />
              </div>
              <div>
                <WorkOrderRoleFilter
                  selectedRoles={selectedRoles}
                  onChange={handleRoleFilterChange}
                  autoInitialize={false}
                />
              </div>
              <div>
                <label htmlFor="work-orders-property-manager" className="block text-sm font-medium text-gray-700 mb-1">
                  Property Manager
                </label>
                <PropertyManagementFilter
                  id="work-orders-property-manager"
                  value={selectedPropertyManagerId}
                  onChange={setSelectedPropertyManagerId}
                  users={propertyManagers}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <FilterToggle
                checked={includeOtherTeamMembers}
                onChange={setIncludeOtherTeamMembers}
                label="Display All Assigned"
              />

              <FilterToggle
                checked={showAllFuture}
                onChange={setShowAllFuture}
                label="Show all future work orders"
              />

              <FilterToggle
                checked={feedbackOnly}
                onChange={setFeedbackOnly}
                label="Feedback Work Orders"
                count={feedbackCount}
              />
            </div>
          </div>
        )}
      </div>

      <ActiveFilterSummary filters={activeFilters} onClearAll={handleClearFilters} />

      <div className="bg-white shadow overflow-hidden rounded-lg">
        {loading && accumulatedWorkOrders.length === 0 ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading work orders...</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-600">
            <p>Error loading work orders: {error.message}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 inline-flex items-center px-3 py-1.5 text-sm border border-red-200 text-red-700 bg-red-50 rounded-md hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        ) : displayedWorkOrders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p>
              {showAllFuture
                ? `No future ${feedbackOnly ? 'feedback ' : ''}work orders found.`
                : `No ${feedbackOnly ? 'feedback ' : ''}work orders for ${format(selectedDate, 'MMMM d, yyyy')}.`}
            </p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-gray-200">
              {displayedWorkOrders.map((workOrder, index) => {
                const assignedToIdAsNumber = parseInt(workOrder.assignedToId, 10);
                const isAssignedToCurrentUser = assignedToIdAsNumber === currentUser?.id;

                return (
                  <WorkOrderListItem
                    key={workOrder.id}
                    workOrder={workOrder}
                    index={index}
                    getStatusColor={getStatusColor}
                    reservationsByProperty={reservationsByProperty}
                    onViewWorkOrder={handleViewWorkOrder}
                    showAssignedBadge={isAssignedToCurrentUser}
                    showFeedbackBadge={workOrder.isFeedback}
                    showAssignmentDetails
                  />
                );
              })}
            </ul>

            {isLoadingMore && (
              <div className="p-4 flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-600 font-medium">Loading more...</span>
              </div>
            )}

            {!isLoadingMore && hasMore && (
              <div className="p-4 bg-gray-50 border-t text-center">
                <button
                  type="button"
                  onClick={loadMore}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Load More
                </button>
              </div>
            )}

            {!hasMore && accumulatedWorkOrders.length > 0 && (
              <div className="p-4 bg-gray-50 border-t text-center text-sm text-gray-500">
                All work orders loaded
              </div>
            )}
          </>
        )}
      </div>

      <WorkOrderDetailSlidePanel
        isOpen={isSlidePanelOpen}
        onClose={handleCloseSlidePanel}
        workOrderId={selectedWorkOrderId}
        onDataChange={handleWorkOrderDataChange}
      />
    </div>
  );
}
